import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { Spinner } from 'src/components/Spinner'
import { ShowDialogProps } from 'src/show-dialog'
import FileViewer from 'react-file-viewer'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { Box, Typography, useTheme } from '@mui/material'
import { GridActionButton } from 'src/components/common/GridActionButton'

interface Props extends ShowDialogProps {
  fileUrl: string
  title?: string
  bottomAction?: (pop: () => void) => React.ReactNode
  fileType?: string
}

export const ShowFileDialog: React.FC<Props> = ({ pop, fileUrl, title, bottomAction, fileType: FType }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [blobUrl, setBlobUrl] = useState<string>('')
  const [padding, setPadding] = useState(0)
  const { t } = useTranslation(['common'])
  const theme = useTheme()
  const ref = useRef<HTMLDivElement>(null)
  const boxRef = useRef<HTMLDivElement>(null)

  const fileType = FType || fileUrl.split('?')[0].split('.').pop()?.toLowerCase() || ''
  const imageExtensions = ['gif', 'jpg', 'jpeg', 'png', 'webp', 'bmp', 'tiff', 'tif', 'svg']
  const isImage = imageExtensions.includes(fileType)
  const isGzip = fileType.startsWith('gz') || fileType.startsWith('gzip')

  const renderUnsupportedComponent = () => {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="80%" gap={2} p={4}>
        <Typography variant="body1" textAlign="center">
          {t('file-type-not-supported', { fileType: fileType ?? '' })}
        </Typography>
      </Box>
    )
  }
  useEffect(() => {
    if (isImage) {
      setLoading(false)
      return
    }

    if (isGzip) {
      const link = document.createElement('a')
      link.href = fileUrl
      link.download = fileUrl.split('/').pop() || 'download.gz'
      document.body.appendChild(link)
      link.click()
      link.remove()
      pop()
    } else {
      const fetchFile = async () => {
        try {
          const response = await fetch(fileUrl, { mode: 'cors' })
          if (!response.ok) throw new Error('Failed to fetch file')

          const blob = await response.blob()
          const url = URL.createObjectURL(blob)
          setBlobUrl(url)
          setLoading(false)
        } catch (e) {
          onError(e as Error)
        }
      }

      fetchFile()

      return () => {
        if (blobUrl) URL.revokeObjectURL(blobUrl)
      }
    }
  }, [fileUrl])

  const onError = (e: Error) => {
    setError(e.message)
    setLoading(false)
  }

  const handleDownload = async () => {
    const fileName = (fileUrl.split('?')[0].split('/').pop() || 'download').replace(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i,
      '',
    )
    if (blobUrl) {
      const link = Object.assign(document.createElement('a'), { href: blobUrl, download: fileName })
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      try {
        const response = await fetch(fileUrl)
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        const link = Object.assign(document.createElement('a'), { href: url, download: fileName })
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch {
        window.open(fileUrl, '_blank')
      }
    }
  }

  if (isGzip) {
    return null
  }

  if (isGzip) {
    return null
  }

  return (
    <GenericDialogLayout
      title={title ?? t('preview-document')}
      pop={pop}
      sx={{ height: '85vh', width: '60vw', display: 'flex', flexDirection: 'column' }}
      titleTypography={{ variant: 'h5', color: getThemeColor(theme, MUIThemeColors.primaryMain) }}
      topActions={[
        <GridActionButton
          key="download"
          buttonText={t('download', { ns: 'common' })}
          onClick={handleDownload}
          disabled={loading || !!error || !fileUrl}
          sx={{ marginRight: 3 }}
        />,
      ]}
    >
      <Box
        ref={boxRef}
        sx={{
          border: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
          borderRadius: '4px',
          flex: 1,
          width: '100%',
          overflow: 'hidden',
          marginBottom: bottomAction ? 2 : 0,
          '& .pg-viewer-wrapper': { overflowY: 'auto' },
        }}
      >
        {loading && <Spinner />}
        {error && <p>{error}</p>}
        {!loading && !error && isImage && (
          <img src={fileUrl} alt={title ?? ''} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        )}
        {!loading && !error && !isImage && blobUrl && (
          <FileViewer
            fileType={fileType}
            filePath={blobUrl}
            onError={onError}
            unsupportedComponent={renderUnsupportedComponent}
          />
        )}
      </Box>
      {bottomAction && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            gap: 1,
          }}
        >
          {bottomAction(pop)}
        </Box>
      )}
    </GenericDialogLayout>
  )
}
